import type { ReactNode } from 'react';
import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import { SectionLabel } from './SectionLabel';
import { CategoryPills, type CategoryPill } from './CategoryPills';
import { ScrollReveal } from './ScrollReveal';

export interface DiscoverHeaderProps {
    /** Eyebrow text, e.g. "(01 – Projects)". */
    eyebrow: string;
    /** Giant display heading, e.g. "Discover Projects". */
    heading: string;
    categoriesLabel?: string;
    categories: CategoryPill[];
    description: string;
    ctaLabel?: string;
    ctaTo?: string;
    /** Renders in place of the CTA pill (e.g. a subscribe field) when provided. */
    actionSlot?: ReactNode;
    /** Extra classes for the display heading (e.g. a tighter tracking override). */
    headingClassName?: string;
    /** dark = black section / white type; light = white section / dark type. */
    theme: 'dark' | 'light';
    /** When true the separator rule spans the full width (eyebrow stays right). */
    fullWidthRule?: boolean;
}

/**
 * Shared editorial header for the Discover sections (Projects, Professionals,
 * Articles): a hairline + eyebrow, a giant display heading, then a
 * popular-categories column beside a description + CTA pill. Flex/vw flow
 * layout so it scales.
 */
export const DiscoverHeader = ({
    eyebrow,
    heading,
    categoriesLabel = 'Popular categories:',
    categories,
    description,
    ctaLabel,
    ctaTo,
    actionSlot,
    headingClassName,
    theme,
    fullWidthRule = false,
}: DiscoverHeaderProps) => {
    const isDark = theme === 'dark';
    return (
        <div
            className={cn(
                'flex flex-col max-md:gap-12 max-md:px-6 max-md:pt-10 md:gap-[13.54vw] md:px-[1.927vw] md:pt-[8.33vw]',
                isDark ? 'bg-black text-white' : 'bg-white text-[#181818]',
            )}
        >
            {/* Design tightens the eyebrow→heading gap on the full-width-rule
                headers (Professionals/Articles/Magazine, 92px) vs Projects (190px).
                Each element reveals in sequence, matching the Home Design Evening
                cadence (eyebrow → heading → body, ~120ms apart). */}
            <div
                className={cn(
                    'flex flex-col max-md:gap-5',
                    fullWidthRule ? 'md:gap-[4.79vw]' : 'md:gap-[9.9vw]',
                )}
            >
                <ScrollReveal>
                    <div className="flex w-full flex-col gap-[0.885vw]">
                        <div
                            className={cn(
                                'h-px',
                                fullWidthRule
                                    ? 'w-full'
                                    : 'w-full md:ml-auto md:w-[47.5vw]',
                                isDark ? 'bg-white/40' : 'bg-black/20',
                            )}
                        />
                        <div
                            className={cn(
                                'w-full',
                                !fullWidthRule && 'md:ml-auto md:w-[47.5vw]',
                            )}
                        >
                            <SectionLabel
                                className={cn(
                                    'tracking-[-0.01em] max-md:text-[1.75vw] md:text-[1.667vw]',
                                    isDark && 'text-white',
                                )}
                            >
                                {eyebrow}
                            </SectionLabel>
                        </div>
                    </div>
                </ScrollReveal>
                <ScrollReveal delay={120}>
                    <h2
                        className={cn(
                            'm-0 font-sans font-normal max-md:text-[11.72vw] max-md:leading-[11.98vw] max-md:-tracking-[0.49vw] md:w-[70.4vw] md:text-[10.417vw] md:leading-[9.375vw]',
                            // Black-on-white headings read looser than white-on-black
                            // at the same value, so light sections track tighter.
                            isDark
                                ? 'md:tracking-[-0.3vw]'
                                : 'md:tracking-[-0.4375vw]',
                            headingClassName,
                        )}
                    >
                        {heading}
                    </h2>
                </ScrollReveal>
            </div>

            <ScrollReveal delay={240}>
                <div className="flex max-md:gap-5 md:gap-[24vw]">
                    <CategoryPills
                        label={categoriesLabel}
                        items={categories}
                        className="max-md:w-[38%] md:w-[24.3vw]"
                    />
                    <div className="flex min-w-0 flex-col max-md:flex-1 max-md:gap-5 md:w-[43.96vw] md:gap-[3.49vw]">
                        <p className="m-0 font-sans font-normal leading-[1.3] max-md:text-[3.13vw] md:text-[2.03vw]">
                            {description}
                        </p>
                        {actionSlot ??
                            (ctaLabel && ctaTo ? (
                                <RoccoLink
                                    to={ctaTo}
                                    className={cn(
                                        'inline-flex w-fit items-center justify-center whitespace-nowrap rounded-full font-sans font-normal tracking-[-0.03em] shadow-[0px_3.243px_11.351px_rgba(0,0,0,0.11)] max-md:px-7 max-md:py-3 max-md:text-base md:h-[3.125vw] md:min-w-[11.99vw] md:px-[1.436vw] md:text-[1.182vw]',
                                        isDark
                                            ? 'bg-white text-black'
                                            : 'bg-[#181818] text-white',
                                    )}
                                >
                                    {ctaLabel}
                                </RoccoLink>
                            ) : null)}
                    </div>
                </div>
            </ScrollReveal>
        </div>
    );
};
